import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Image as ImageIcon, X as XIcon, Filter } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { AddPerformancePhotoSheet } from '@/components/construction/AddPerformancePhotoSheet';
import { AuthImg } from '@/components/construction/AuthImg';

interface Photo {
  id: string;
  villaId: string | null;
  stageDefId: string | null;
  photoUrl: string;
  caption: string;
  takenAt: string;
  villa: { id: string; villaName: string; model: string } | null;
  stageDef: { id: string; code: string; nameEn: string } | null;
}

const fmtDateTime = (s: string) => new Date(s).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function PerformanceGalleryPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [lightbox, setLightbox] = useState<Photo | null>(null);

  // filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [villaFilter, setVillaFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const r = await apiClient.get<Photo[]>(`/api/v1/construction/projects/${projectId}/performance-photos${params.toString() ? `?${params}` : ''}`);
      setPhotos(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, from, to]);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => photos.filter((p) => {
    if (villaFilter && p.villa?.villaName.toLowerCase().includes(villaFilter.toLowerCase()) === false) return false;
    if (stageFilter && (p.stageDef?.code ?? '').toLowerCase().includes(stageFilter.toLowerCase()) === false) return false;
    return true;
  }), [photos, villaFilter, stageFilter]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to={`/dashboard/construction`}>
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </Link>
          <div className="text-sm font-medium">Work photos</div>
          <Button size="sm" className="gap-2" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">{filtered.length} photo{filtered.length === 1 ? '' : 's'}</div>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setShowFilters((s) => !s)}>
            <Filter className="h-3.5 w-3.5" /> Filter
          </Button>
        </div>

        {showFilters && (
          <div className="border border-border rounded-lg p-3 space-y-3 bg-muted/20">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="flt-from">From</Label>
                <Input id="flt-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="flt-to">To</Label>
                <Input id="flt-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="flt-villa">Villa</Label>
                <Input id="flt-villa" placeholder="V-114" value={villaFilter} onChange={(e) => setVillaFilter(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="flt-stage">Stage</Label>
                <Input id="flt-stage" placeholder="S1.1" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} />
              </div>
            </div>
            {(from || to || villaFilter || stageFilter) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFrom(''); setTo(''); setVillaFilter(''); setStageFilter(''); }}
              >
                Clear filters
              </Button>
            )}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && !loading && (
          <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm p-3">{error}</div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No work photos yet. Tap Add to upload the first.
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setLightbox(p)}
              className="text-left rounded-lg overflow-hidden border border-border hover:border-foreground transition-colors"
            >
              <div className="aspect-square bg-muted">
                <AuthImg src={p.photoUrl} alt={p.caption} className="h-full w-full object-cover" />
              </div>
              <div className="p-2 space-y-0.5">
                <div className="text-xs font-medium line-clamp-2">{p.caption}</div>
                <div className="text-[10px] text-muted-foreground">
                  {p.villa?.villaName ?? '—'}{p.stageDef ? ` · ${p.stageDef.code}` : ''}
                </div>
              </div>
            </button>
          ))}
        </div>
      </main>

      {projectId && (
        <AddPerformancePhotoSheet open={addOpen} onClose={() => setAddOpen(false)} onCreated={refresh} projectId={projectId} />
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 h-10 w-10 inline-flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
          >
            <XIcon className="h-5 w-5" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full text-center">
            <AuthImg src={lightbox.photoUrl} alt={lightbox.caption} className="max-w-full max-h-[78vh] object-contain rounded" loading="eager" />
            <div className="mt-3 text-white text-sm space-y-1">
              <div className="font-medium">{lightbox.caption}</div>
              <div className="text-xs opacity-80">
                {fmtDateTime(lightbox.takenAt)}
                {lightbox.villa ? ` · ${lightbox.villa.villaName} (${lightbox.villa.model})` : ''}
                {lightbox.stageDef ? ` · ${lightbox.stageDef.code} — ${lightbox.stageDef.nameEn}` : ''}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
